/* DraftHistoryPanel — shows the last N AI drafts for a slide with one-click
   restore. Backed by GET /pitch/decks/{id}/slide/{n}/draft-history and
   POST /pitch/decks/{id}/slide/{n}/restore-draft on the backend.

   Restore is FREE — the credits originally spent on the AI drafts are NOT
   refunded (this only swaps the content, matching the AI-draft undo
   policy). Restoring pushes the current content back into history in
   place of the restored entry, so the swap is reversible.
*/

import React, { useEffect, useState, useCallback } from "react";
import { ClockCounterClockwise, ArrowCounterClockwise, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { extractErrorMessage } from "@/lib/apiError";

const TEAL_SOFT = "#7BD3CE";
const ORANGE = "#F59E0B";

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function bulletsFromDraft(text) {
  return (text || "")
    .split("\n")
    .map((l) => l.replace(/^[-*•●▪]\s*/, "").replace(/^\d+[.)]\s*/, "").trim())
    .filter(Boolean);
}

export default function DraftHistoryPanel({ deckId, slideNum, onRestored }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pitch/decks/${deckId}/slide/${slideNum}/draft-history`);
      setHistory(data?.history || []);
    } catch (err) {
      // 401/other errors: silently show empty; user will see "No history"
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [deckId, slideNum]);

  // Refresh whenever the panel is opened OR the slide changes while open.
  useEffect(() => {
    if (open) fetchHistory();
  }, [open, fetchHistory]);

  // Also refresh when a new draft is created elsewhere in the editor.
  useEffect(() => {
    const handler = () => { if (open) fetchHistory(); };
    window.addEventListener("clinq:draft-history-changed", handler);
    return () => window.removeEventListener("clinq:draft-history-changed", handler);
  }, [open, fetchHistory]);

  const restore = async (entry) => {
    setRestoringId(entry.id);
    try {
      const { data } = await api.post(
        `/pitch/decks/${deckId}/slide/${slideNum}/restore-draft`,
        { entry_id: entry.id },
      );
      setHistory(data?.history || []);
      onRestored?.({ content: data.content, findings: data.findings || [] });
      toast.success("Restored earlier draft", {
        description: "Credits already spent on drafts are not refunded.",
        duration: 5000,
      });
    } catch (err) {
      if (!err.isCreditShortage) toast.error(extractErrorMessage(err, "Restore failed. Please try again."));
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <div className="mt-3 rounded-lg" data-testid="draft-history-panel"
      style={{ backgroundColor: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.08)" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="draft-history-toggle"
        className="w-full flex items-center justify-between px-3.5 py-2.5 text-left hover:bg-white/[0.03] transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold" style={{ color: TEAL_SOFT }}>
          <ClockCounterClockwise size={13} weight="bold" />
          <span>Draft history</span>
          {history.length > 0 && (
            <span className="text-white/45 font-mono-num normal-case tracking-normal">· {history.length}</span>
          )}
        </div>
        <CaretDown
          size={13}
          weight="bold"
          style={{
            color: "rgba(255,255,255,0.5)",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </button>

      {open && (
        <div className="px-3.5 pb-3">
          {loading && (
            <div className="text-[11px] text-white/45 italic py-2">Loading history…</div>
          )}

          {!loading && history.length === 0 && (
            <div className="text-[11px] text-white/50 leading-relaxed py-2" data-testid="draft-history-empty">
              No previous AI drafts on this slide yet. Each time you re-draft with AI, the old version is saved here so you can restore it later — up to the last 5 versions.
            </div>
          )}

          {!loading && history.length > 0 && (
            <>
              <div className="text-[10.5px] text-white/45 mb-2 leading-relaxed">
                Newest first · restoring swaps your current draft into history, so the change is reversible. <span className="text-white/60 font-semibold">Credits are not refunded.</span>
              </div>
              <div className="space-y-2">
                {history.slice().reverse().map((entry, i) => {
                  const points = bulletsFromDraft(entry.content);
                  const isRestoring = restoringId === entry.id;
                  return (
                    <div
                      key={entry.id}
                      data-testid={`draft-history-entry-${i}`}
                      className="rounded-md px-3 py-2.5 flex items-start gap-3 group"
                      style={{ backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-[10px] font-mono-num text-white/45">
                          <span>{timeAgo(entry.created_at)}</span>
                          <span className="text-white/25">·</span>
                          <span className="uppercase tracking-widest text-white/40">
                            {entry.source === "restore_swap" ? "swapped in" : "auto-saved"}
                          </span>
                          <span className="text-white/25">·</span>
                          <span className="text-white/40 normal-case">
                            {points.length} {points.length === 1 ? "bullet" : "bullets"}
                          </span>
                        </div>
                        {points.length > 0 ? (
                          <ul className="mt-1.5 space-y-1"
                            data-testid={`draft-history-entry-bullets-${i}`}>
                            {points.map((b, j) => (
                              <li key={j} className="flex items-start gap-1.5 text-[12px] text-white/78 leading-snug">
                                <span className="inline-block shrink-0 mt-1.5" style={{ width: 4, height: 4, backgroundColor: ORANGE, opacity: 0.85 }} />
                                <span className="min-w-0">{b}</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="text-[12px] mt-1 italic text-white/40">(empty draft)</div>
                        )}
                      </div>
                      <button
                        onClick={() => restore(entry)}
                        disabled={isRestoring}
                        data-testid={`draft-history-restore-${i}`}
                        className="text-[10.5px] font-bold uppercase tracking-[0.14em] px-2.5 py-1.5 rounded inline-flex items-center gap-1.5 shrink-0 disabled:opacity-60 transition-all"
                        style={{
                          backgroundColor: "rgba(245, 158, 11, 0.14)",
                          color: ORANGE,
                          border: "1px solid rgba(245, 158, 11, 0.35)",
                        }}
                        onMouseEnter={(e) => { if (!isRestoring) e.currentTarget.style.backgroundColor = "rgba(245, 158, 11, 0.26)"; }}
                        onMouseLeave={(e) => { if (!isRestoring) e.currentTarget.style.backgroundColor = "rgba(245, 158, 11, 0.14)"; }}
                        title="Restore this draft into the editor (your current draft is pushed into history in its place)"
                      >
                        <ArrowCounterClockwise size={11} weight="bold" />
                        {isRestoring ? "…" : "Restore"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
